import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { categories, productsByCategory } from "../../data/productData";

// Product Card Component
const ProductCard = ({ product, categoryLink, index }) => {
  const navigate = useNavigate();
  const productId = product.id || encodeURIComponent((product.name || "").replace(/\s+/g, "_").toLowerCase());

  const handleClick = () => {
    navigate(`/our-products/${categoryLink}/${productId}`);
  };

  return (
    <div
      className="group product-card bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
      style={{ animationDelay: `${index * 100}ms` }}
      onClick={handleClick}>
      <div className="relative h-48 overflow-hidden">
        <img
          src={product.image || "/assets/productimages/placeholder.jpg"}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            e.target.src = "/assets/productimages/placeholder.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Hover Overlay */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,163,161,0.12)" }}>
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 transform scale-0 group-hover:scale-100 transition-transform duration-300">
            <svg
              className="w-6 h-6 icon-accent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 card-title transition-colors duration-200">
          {product.name}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2">{product.use || product.description}</p>
      </div>
    </div>
  );
};

// Breadcrumb Component
const Breadcrumbs = ({ categoryName, subcategoryName, categoryLink }) => (
  <nav
    className="breadcrumbs mb-6"
    aria-label="Breadcrumb">
    <ol className="flex items-center flex-wrap">
      <li>
        <Link
          to="/"
          className="hover:underline">
          Home
        </Link>
      </li>
      <li aria-hidden>
        <svg
          className="w-4 h-4 text-gray-400 mx-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </li>
      <li>
        <Link
          to="/our-products"
          className="hover:underline">
          Products
        </Link>
      </li>
      <li aria-hidden>
        <svg
          className="w-4 h-4 text-gray-400 mx-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </li>
      <li>
        <Link
          to={`/our-products/${categoryLink}`}
          className="hover:underline">
          {categoryName}
        </Link>
      </li>
      <li aria-hidden>
        <svg
          className="w-4 h-4 text-gray-400 mx-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </li>
      <li className="crumb-current">{subcategoryName}</li>
    </ol>
  </nav>
);

// Main Subcategory Page Component
const SubcategoryPage = () => {
  const { category, subcategory } = useParams();

  // Find category metadata
  const categoryMeta = categories.find((c) => c.link === category) || {};
  const categoryName = categoryMeta.name || category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  // Find subcategory metadata
  const subcategoryMeta = categoryMeta.subcategories?.find((s) => {
    const slug = String(s.slug || s.id || s.name)
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    return slug === subcategory;
  });

  const subcategoryName =
    subcategoryMeta?.name || subcategory.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  const subcategoryImage = subcategoryMeta?.image || "/assets/productimages/placeholder.jpg";
  const subcategoryDescription =
    subcategoryMeta?.description ||
    `Explore our comprehensive range of ${subcategoryName.toLowerCase()} products. We offer high-quality solutions designed to meet all your construction and renovation needs with superior durability and performance.`;

  // Get products for this subcategory
  const categoryProducts = productsByCategory[category];

  const getProductsForSubcategory = () => {
    if (!categoryProducts) return [];

    // Try to find products by matching subcategory name
    const subcatKey = Object.keys(categoryProducts).find((key) => {
      const keySlug = key
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      return keySlug === subcategory || key.toLowerCase() === subcategoryName.toLowerCase();
    });

    if (subcatKey) {
      const entry = categoryProducts[subcatKey];

      // Direct array of products
      if (Array.isArray(entry)) return entry;

      // Object with products array
      if (entry?.products) return entry.products;

      // Object with options (nested structure)
      if (entry?.options) {
        const allProducts = [];
        Object.values(entry.options).forEach((opt) => {
          if (opt?.products) {
            allProducts.push(...opt.products);
          } else if (opt?.options) {
            Object.values(opt.options).forEach((nestedOpt) => {
              if (nestedOpt?.products) {
                allProducts.push(...nestedOpt.products);
              }
            });
          }
        });
        return allProducts;
      }
    }

    return [];
  };

  const products = getProductsForSubcategory();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* Breadcrumbs */}
        <Breadcrumbs
          categoryName={categoryName}
          subcategoryName={subcategoryName}
          categoryLink={category}
        />

        {/* Subcategory Name */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 mt-6 mb-12 uppercase">
          {subcategoryName}
        </h1>

        {/* Subcategory Info Section - Description Left, Image Right */}
        <section className="bg-white rounded-2xl p-4 sm:p-6 md:p-10 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
            {/* Description - Left Side */}
            <div className="order-2 lg:order-1">
              <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6">{subcategoryDescription}</p>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base btn-accent-fill transition-all duration-300">
                  Request Quote
                  <svg
                    className="w-4 sm:w-5 h-4 sm:h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
                <Link
                  to={`/our-products/${category}`}
                  className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base btn-accent transition-all duration-300">
                  View All {categoryName}
                </Link>
              </div>
            </div>

            {/* Image - Right Side */}
            <div className="order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src={subcategoryImage}
                  alt={subcategoryName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/assets/productimages/placeholder.jpg";
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="mt-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-10 gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Products in {subcategoryName}</h2>
              <p className="text-gray-500 mt-1">
                {products.length} {products.length === 1 ? "product" : "products"} available
              </p>
            </div>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {products.map((product, index) => (
                <ProductCard
                  key={product.id || index}
                  product={product}
                  categoryLink={category}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-12 text-center">
              <svg
                className="w-16 h-16 mx-auto text-gray-300 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Products Coming Soon</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We're currently updating our product catalog for this subcategory. Please contact us for specific
                product inquiries.
              </p>
              <Link
                to="/contact-us"
                className="inline-flex items-center px-6 py-3 rounded-lg font-semibold btn-accent-fill transition-all duration-300">
                Contact Us
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          )}
        </section>

        {/* Back Navigation */}
        <div className="mt-16 pt-10 border-t border-gray-200">
          <Link
            to={`/our-products/${category}`}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to {categoryName}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryPage;
